/**
 * Google・Apple から戻ったあと（/oauth/continue、#112、docs/43 §3）。
 *   API の戻り口が Cookie（5分の引き継ぎ）を置いてここへ送る。ここで POST /auth/oauth/continue を1回だけ呼ぶ：
 *   ・海外の IP なら確認コード（api.ts が確認コードの窓を開いて送り直す＝docs/42 と同じ）
 *   ・会社が1つ → その会社のホーム（招待のリンクから来たならその画面）
 *   ・会社が2つ以上・この製品の会社がまだ無い → 会社を選ぶ（「＋ 新しい会社を作る」もそこ）
 *   ・断られた（退会の手続き中など）→ 文とログインの画面への入口
 */
import { OAUTH_MSG, type ChooseResponse, type Me } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { setMe } from "../auth.js";
import { takeOAuthAfter } from "../components/OAuthButtons.jsx";
import { markJustLoggedIn } from "../components/Passkey.jsx";
import { AuthCard, ErrorText } from "../components/ui.jsx";
import { afterLoginPath } from "../lib/autoLogout.js";

export function OAuthContinuePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | undefined>();
  // StrictMode（開発）で2回走らないように。引き継ぎは1回きり
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        const res = await api<(Me & { next?: string | null }) | ChooseResponse>("POST", "/auth/oauth/continue");
        const after = takeOAuthAfter();
        markJustLoggedIn(); // ログイン直後の案内「次回からパスキーでログイン」（#113）
        if ("choose" in res) {
          nav("/choose", { replace: true, state: res });
          return;
        }
        await setMe(qc, res);
        void qc.invalidateQueries();
        nav(after ?? res.next ?? afterLoginPath(), { replace: true });
      } catch (e) {
        if (e instanceof ApiError && e.code === "AUTH_ACCOUNT_CLOSED") {
          // 退会の手続き中：ログインの画面の案内（いつまで戻せるか）をそのまま使う
          nav("/login", { replace: true, state: { closed: { email: String(e.problem.email ?? ""), purgeAfter: typeof e.problem.purgeAfter === "string" ? e.problem.purgeAfter : "" } } });
          return;
        }
        setErr(messageOf(e));
      }
    })();
  }, [nav, qc]);

  return (
    <AuthCard title={err ? OAUTH_MSG.continueFailed : OAUTH_MSG.continuing}>
      {err ? (
        <>
          <ErrorText>{err}</ErrorText>
          <Link className="btn big primary" to="/login" data-testid="oauth-back">
            {OAUTH_MSG.backToLogin}
          </Link>
        </>
      ) : (
        <p className="hint" role="status" data-testid="oauth-continuing">
          {OAUTH_MSG.continuing}
        </p>
      )}
    </AuthCard>
  );
}
